import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaUser } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import BuildStamp from "./BuildStamp";
import SendFeedback from "./SendFeedback";
import "./UserMenu.css";

function signedInLabel(user) {
  const display = user?.display_name?.trim();
  const username = user?.username?.trim();
  return display || username || "";
}

function signedInDetail(user) {
  const display = user?.display_name?.trim();
  const username = user?.username?.trim();
  if (display && username && display !== username) return username;
  return "";
}

function roleLabel(role) {
  if (!role) return "";
  const text = String(role);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export default function UserMenu({
  isDarkMode,
  onToggleTheme,
  onLogout,
  mode,
  onSwitchMode,
}) {
  const [open, setOpen] = useState(false);
  const [feedbackOpened, setFeedbackOpened] = useState(0);
  const [box, setBox] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const { user } = useAuth();
  const who = signedInLabel(user);
  const detail = signedInDetail(user);
  const role = roleLabel(user?.role);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setBox({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    };
    const onPointer = (event) => {
      const target = event.target;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    place();
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  const run = (action) => {
    setOpen(false);
    action();
  };

  return (
    <div className="user-menu" ref={ rootRef }>
      <button
        ref={ buttonRef }
        type="button"
        className="user-menu-button"
        aria-label={ who ? `Account, ${who}` : "Account" }
        aria-haspopup="menu"
        aria-expanded={ open }
        onClick={ () => setOpen((value) => !value) }
      >
        <FaUser aria-hidden="true" />
      </button>
      { open && box && createPortal(
        <div
          className="user-menu-panel"
          role="menu"
          ref={ panelRef }
          style={ { top: box.top, right: box.right } }
        >
          { who && (
            <div className="user-menu-who">
              <strong>{ who }</strong>
              { detail && <span>{ detail }</span> }
              { role && <span>{ role }</span> }
            </div>
          ) }
          { onSwitchMode && (
            <button type="button" role="menuitem" onClick={ () => run(onSwitchMode) }>
              { mode === "technician" ? "Office view" : "Technician view" }
            </button>
          ) }
          <button type="button" role="menuitem" onClick={ () => run(onToggleTheme) }>
            { isDarkMode ? "Light mode" : "Dark mode" }
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={ () => {
              setOpen(false);
              setFeedbackOpened((count) => count + 1);
            } }
          >
            Feedback
          </button>
          <button type="button" role="menuitem" onClick={ () => run(onLogout) }>
            Logout
          </button>
          <div className="user-menu-stamp">
            <BuildStamp />
          </div>
        </div>,
        document.body
      ) }
      <SendFeedback hideTrigger opened={ feedbackOpened } />
    </div>
  );
}
